import { defaultConfig } from '@tamagui/config/v5';
import { animations } from '@tamagui/config/v5-rn';
import { createFont, createTamagui } from 'tamagui';

import { color, fontSize, tokens } from '@/constants/tokens';

/**
 * ── Tamagui config for the Putting Scene rehaul ───────────────────────
 *
 * Design values come from `constants/tokens.ts` (the single source of truth) —
 * this file only wires them into Tamagui: it merges our token groups over the
 * v5 preset, defines the two font families, defines ONE theme (`light`) whose
 * semantic aliases (`$background`, `$borderColor`, `$color`) point at brand
 * tokens, and turns on strict style-value validation.
 *
 * No light/dark switching for now — the app hardcodes `defaultTheme="light"` in
 * app/_layout.tsx. `$background`/`$borderColor`/`$color` are theme values (they
 * can't exist without a theme), so we keep a single theme to give them a home.
 *
 * The one theme MUST be named `light` (a color-scheme name), not a brand name
 * like `ps`. On web the CSS generator (getThemeCSSRules) only emits a real
 * selector for a base theme when its name is `light`/`dark` — because the v5
 * preset turns on `shouldAddPrefersColorThemes`. A non-scheme name makes it emit
 * an EMPTY selector, so the theme's CSS vars ($background/$brand/…) never attach
 * to any DOM node and every themed color renders unstyled on web (native is fine
 * — it resolves themes in JS at runtime and ignores this CSS entirely).
 *
 * Runtime-only (no @tamagui/babel-plugin / metro optimizing compiler needed for
 * correctness) — the Metro plugin is wired for web CSS extraction, not required
 * for the tokens to work.
 * ──────────────────────────────────────────────────────────────────────
 */

// Font weight → loaded face. GeneralSans ships two real weights (Medium 500,
// Semibold 600); every other numeric weight is mapped onto one of them so
// nothing falls back to a missing face on native. Keys MUST match FONT_ASSETS.
const generalSansFace = {
  '100': { normal: 'GeneralSans-Medium' },
  '200': { normal: 'GeneralSans-Medium' },
  '300': { normal: 'GeneralSans-Medium' },
  '400': { normal: 'GeneralSans-Medium' },
  normal: { normal: 'GeneralSans-Medium' },
  '500': { normal: 'GeneralSans-Medium' },
  '600': { normal: 'GeneralSans-Semibold' },
  '700': { normal: 'GeneralSans-Semibold' },
  '800': { normal: 'GeneralSans-Semibold' },
  '900': { normal: 'GeneralSans-Semibold' },
  bold: { normal: 'GeneralSans-Semibold' },
};

// Gochi Hand: one loaded file, so every weight resolves to it.
const gochiFace = Object.fromEntries(
  [
    '100',
    '200',
    '300',
    '400',
    '500',
    '600',
    '700',
    '800',
    '900',
    'normal',
    'bold',
  ].map((w) => [w, { normal: 'GochiHand' }])
);

// Font sizes come from constants/tokens.ts (kept out of the `size` token group
// on purpose — see the note there). These keys are the fontSize token names Text
// reads via `fontSize="$base"`.
const generalSans = createFont({
  family: 'GeneralSans-Medium',
  size: fontSize,
  lineHeight: { small: 16, base: 20, large: 32, true: 20 },
  weight: { normal: '400', medium: '500', semibold: '600', true: '500' },
  face: generalSansFace,
});

const gochiHand = createFont({
  family: 'GochiHand',
  size: fontSize,
  // Handwritten faces need extra vertical room over their nominal size.
  lineHeight: { small: 17, base: 20, large: 35, true: 20 },
  weight: { normal: '400', medium: '400', semibold: '400', true: '400' },
  face: gochiFace,
});

// Base `Text` → body font. Kept as a standalone const so it can be spread into
// the config with its type erased (see the note at the spread site below).
const DEFAULT_PROPS = {
  defaultProps: {
    Text: {
      fontFamily: '$body',
      fontSize: '$base',
    },
  },
};

export const tamaguiConfig = createTamagui({
  ...defaultConfig,

  // The animation driver. The v5 preset ships NO `animations` key, and without a
  // driver every Tamagui component that animates either silently skips the
  // animation or throws outright — @tamagui/toast throws ("Toast requires an
  // animation driver to be set in TamaguiProvider"), which is what put this here.
  //
  // We take the preset's react-native driver (RN's own Animated) rather than the
  // CSS/Reanimated ones: it needs no extra dependency and works on native and web
  // alike, and nothing in the app animates hot enough to need off-thread values.
  //
  // Use the PRESET, not a hand-rolled createAnimations: Tamagui's own components
  // ask for transitions by duration NAME (`transition="400ms"`), and a name that
  // isn't registered in this map is treated as raw CSS and dropped on native. The
  // preset registers the whole `0ms`…`500ms` set plus the spring names, so those
  // internal requests resolve.
  animations,

  // Merge our token groups over the v5 preset. color/space/radius/borderWidth
  // come from constants/tokens.ts; everything else (size, zIndex, negative
  // space, etc.) stays as the preset provides it. Font sizes are NOT a token
  // group here — they live in createFont above.
  tokens: {
    ...defaultConfig.tokens,
    ...tokens,
  },

  fonts: {
    ...defaultConfig.fonts,
    heading: generalSans,
    body: generalSans,
    hand: gochiHand,
  },

  // Default the base `Text` primitive to our body font. The `tamagui` <Text> is
  // styled with only `color: '$color'` (no fontFamily), so a bare <Text> falls
  // back to the iOS system font despite `defaultFont`. Setting it here applies
  // GeneralSans app-wide without touching every screen.
  //
  // Spread (not an inline key) on purpose: `defaultProps` is honored at runtime
  // but is NOT in `createTamagui`'s argument type. Written inline, the excess
  // property defeats the call's return-type inference — `tamaguiConfig` collapses
  // to `any`, `Conf` self-references, and every $token elsewhere reads as invalid.
  // Spreading a value typed `Record<string, unknown>` injects it without feeding
  // the excess-property check, so inference (and all downstream $token types)
  // survives. See the no-linter-workarounds rule — suppress the false positive,
  // don't restructure working runtime code.
  ...(DEFAULT_PROPS as Record<string, unknown>),
  // defaultProps: {
  //   Text: {
  //     fontFamily: 'body',
  //   },
  // },

  // ONE theme, named `light`. `$background`/`$borderColor`/`$color` are the
  // semantic names screens use for surfaces/borders/text; they resolve to brand
  // tokens. No light/dark variants — we replace the preset's themes entirely
  // with just this. The `light` name is load-bearing for web CSS (see the
  // file-header note): a non-scheme name breaks the generated theme selector.
  themes: {
    light: {
      background: color.background,
      borderColor: color.borderColor,
      color: color.textDark,
    },
  },

  settings: {
    ...defaultConfig.settings,
    // Longhand style props (backgroundColor, borderRadius, …) stay available so
    // screens read like normal RN rather than being locked to shorthands.
    onlyAllowShorthands: false,
    // STRICT: token-enabled style props (color/space/size/radius) only accept
    // token values at the type level. A raw hex/px is a type error. Note this is
    // type-level only — it doesn't stop the bundle by itself; the ESLint rule
    // (next task) is what fails compilation. borderWidth is NOT covered here (not
    // a token-enabled prop) — the linter enforces that one.
    //
    // Negative spacing (e.g. a bleed of -$xl): Tamagui has no `-$token` STRING
    // type in v2 — strictness only widens the numeric/percent fallback, never
    // adds a negative-token string. Get a negative token value the numeric way:
    //   import { space } from '@/constants/tokens'; marginHorizontal={-space.xl}
    // That reads a token (no raw literal), so it passes both strict mode and the
    // no-hardcoded-tokens linter.
    allowedStyleValues: 'strict',
  },
});

export type Conf = typeof tamaguiConfig;

// `declare module 'tamagui'` merges our config's type (Conf) into Tamagui's
// global types, so `$brand`, `fontSize="$base"`, etc. autocomplete and typecheck
// everywhere without importing the config. (See unknown.txt.)
declare module 'tamagui' {
  interface TamaguiCustomConfig extends Conf {}
}

export default tamaguiConfig;
